// src/components/CopyButton.jsx
import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * Reusable copy-to-clipboard button with visual feedback
 * @param {string} value - The text to copy to clipboard
 * @param {string} label - Label for the tooltip (default: 'ID')
 * @param {string} className - Additional CSS classes
 * @param {boolean} showValue - Whether to show the value next to the button
 * @param {string} valueClassName - CSS classes for the value display
 */
const CopyButton = ({
    value,
    label = 'ID',
    className = '',
    showValue = false,
    valueClassName = ''
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            {showValue && (
                <code className={`font-mono text-xs ${valueClassName}`}>
                    {value}
                </code>
            )}
            <button
                onClick={handleCopy}
                className={`p-1 transition-colors ${
                    copied
                        ? 'text-green-700'
                        : 'text-gray-400 hover:text-white'
                }`}
                title={copied ? 'Copied!' : `Copy ${label}`}
            >
                {copied ? (
                    <CheckIcon className="h-4 w-4" />
                ) : (
                    <ClipboardDocumentIcon className="h-4 w-4" />
                )}
            </button>
        </div>
    );
};

/**
 * ID display row with copy button - for use in detail modals
 * @param {string} label - Label text (e.g., "Company ID", "User ID")
 * @param {string} value - The ID value
 */
export const IdField = ({ label, value }) => {
    return (
        <div className="flex items-center justify-between bg-gray-800 border border-gray-700 px-3 py-2 mb-4">
            <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-400 uppercase">{label}</span>
                <code className="text-xs font-mono text-gray-300">{value}</code>
            </div>
            <CopyButton value={value} label={label} />
        </div>
    );
};

export default CopyButton;
